import {Card} from 'platform/components';
import {
  Box,
  Center,
  Grid,
  HStack,
  Show,
  Text,
  type ThemeColorPath,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import type {ArticleAbcXyz} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

const ABC_CLASSES = ['A', 'B', 'C'] as const;
const XYZ_CLASSES = ['X', 'Y', 'Z'] as const;
type AbcClass = (typeof ABC_CLASSES)[number];
type XyzClass = (typeof XYZ_CLASSES)[number];

const CLASS_DESCRIPTIONS = {
  A: 'entity.warehouse.reporting.highValueItems',
  B: 'entity.warehouse.reporting.mediumValueItems',
  C: 'entity.warehouse.reporting.lowValueItems',
  X: 'entity.warehouse.reporting.stableDemand',
  Y: 'entity.warehouse.reporting.variableDemand',
  Z: 'entity.warehouse.reporting.irregularDemand',
} as const satisfies Record<AbcClass | XyzClass, string>;

// 40 px, the height of a matrix cell.
const CELL_HEIGHT = 10;
// 128 px, room for the class letter and its description in front of a row.
const ROW_HEADER_WIDTH = 32;

type CellState = 'empty' | 'partial' | 'active';

// The cell of the article stands out. When only one class is known, its whole row or column is
// tinted. The other cells only outline the matrix.
const CELL_APPEARANCE = {
  empty: {
    border: '1px dashed',
    borderColor: 'general.separator',
    backgroundColor: 'palettes.neutral.10.100',
  },
  partial: {
    border: '1px dashed',
    borderColor: 'palettes.blue.40.100',
    backgroundColor: 'palettes.blue.10.100',
  },
  active: {
    border: '1px solid',
    borderColor: 'palettes.blue.80.100',
    backgroundColor: 'palettes.blue.20.100',
  },
} as const satisfies Record<
  CellState,
  {border: string; borderColor: ThemeColorPath; backgroundColor: ThemeColorPath}
>;

interface StatisticsAbcXyzProps {
  classification: ArticleAbcXyz;
  currency: string;
  handlingUnitLabel: string;
}

interface MetricProps {
  label: string;
  value: string;
}

function Metric(props: MetricProps) {
  return (
    <HStack spacing={3} justify="space-between">
      <Text size="xSmall" color="tertiary">
        {props.label}
      </Text>
      <Text size="xSmall" alternative align="right">
        {props.value}
      </Text>
    </HStack>
  );
}

interface MetricPanelProps {
  children: ReactNode;
}

function MetricPanel(props: MetricPanelProps) {
  return (
    <Box
      padding={4}
      borderRadius="medium"
      border="1px solid"
      borderColor="general.separator"
      backgroundColor="palettes.neutral.20.100"
    >
      <VStack spacing={2}>{props.children}</VStack>
    </Box>
  );
}

interface MatrixCellProps {
  code: string;
  state: CellState;
}

function MatrixCell(props: MatrixCellProps) {
  return (
    <Box height={CELL_HEIGHT} borderRadius="small" {...CELL_APPEARANCE[props.state]}>
      <Center height="100%">
        <Show when={props.state === 'active'}>
          <Text alternative color="link">
            {props.code}
          </Text>
        </Show>
      </Center>
    </Box>
  );
}

interface ClassHeaderProps {
  code: AbcClass | XyzClass;
  /** The class of the article, in the colour of its matrix cell. */
  isHighlighted: boolean;
  /** A row header stands right next to its cells, a column header is centred over them. */
  align: 'flex-end' | 'center';
}

function ClassHeader(props: ClassHeaderProps) {
  return (
    <VStack align={props.align}>
      <Text size="xSmall" alternative color={props.isHighlighted ? 'link' : 'secondary'}>
        {props.code}
      </Text>
      <Text
        size="xSmall"
        color={props.isHighlighted ? 'link' : 'tertiary'}
        align={props.align === 'center' ? 'center' : 'right'}
      >
        {i18n.t(CLASS_DESCRIPTIONS[props.code])}
      </Text>
    </VStack>
  );
}

export function StatisticsAbcXyz(props: StatisticsAbcXyzProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDate = useDateTimeFormatter();
  const number = (value: number | null, digits: number) =>
    value === null ? EMPTY_PLACEHOLDER : (formatNumber(value, digits) ?? EMPTY_PLACEHOLDER);
  const percent = (value: number | null) =>
    value === null ? EMPTY_PLACEHOLDER : (formatNumber(value, 2) ?? EMPTY_PLACEHOLDER) + ' %';
  const date = (value: string | null) =>
    value === null ? EMPTY_PLACEHOLDER : formatDate('dateShort', parseDate(value));
  const period = (start: string | null, end: string | null) =>
    start === null || end === null ? EMPTY_PLACEHOLDER : date(start) + ' – ' + date(end);

  // A status such as "no consumption" is no class of the matrix.
  const abcClass = ABC_CLASSES.find((code) => code === props.classification.abcClass);
  const xyzClass = XYZ_CLASSES.find((code) => code === props.classification.xyzClass);
  const cellStateOf = (row: AbcClass, column: XyzClass) =>
    match({
      isRow: abcClass === row,
      isColumn: xyzClass === column,
      hasAbc: isNotNil(abcClass),
      hasXyz: isNotNil(xyzClass),
    })
      .with({isRow: true, isColumn: true}, () => 'active' as const)
      .with({isRow: true, hasXyz: false}, () => 'partial' as const)
      .with({isColumn: true, hasAbc: false}, () => 'partial' as const)
      .otherwise(() => 'empty' as const);

  return (
    <Card
      title={i18n.t('analytics.label.abcXyzAnalysis')}
      parameters={[
        `${i18n.t('entity.warehouse.reporting.abcClass')}: ${props.classification.abcClass ?? EMPTY_PLACEHOLDER}`,
        `${i18n.t('entity.warehouse.reporting.xyzClass')}: ${props.classification.xyzClass ?? EMPTY_PLACEHOLDER}`,
      ]}
      isFullHeight
    >
      {props.classification.abcClass === null && props.classification.xyzClass === null ? (
        <Text>{i18n.t('general.labels.noData')}</Text>
      ) : (
        <VStack spacing={4}>
          <VStack spacing={1}>
            <HStack spacing={2}>
              <Box width={ROW_HEADER_WIDTH} flexShrink={0} />
              <Box flex={1}>
                <Grid columns={3} spacing={1}>
                  {XYZ_CLASSES.map((column) => (
                    <ClassHeader
                      key={column}
                      code={column}
                      isHighlighted={xyzClass === column}
                      align="center"
                    />
                  ))}
                </Grid>
              </Box>
            </HStack>
            {ABC_CLASSES.map((row) => (
              <HStack key={row} spacing={2} align="center">
                <Box width={ROW_HEADER_WIDTH} flexShrink={0}>
                  <ClassHeader code={row} isHighlighted={abcClass === row} align="flex-end" />
                </Box>
                <Box flex={1}>
                  <Grid columns={3} spacing={1}>
                    {XYZ_CLASSES.map((column) => (
                      <MatrixCell
                        key={column}
                        code={row + column}
                        state={cellStateOf(row, column)}
                      />
                    ))}
                  </Grid>
                </Box>
              </HStack>
            ))}
          </VStack>
          <Grid columns={[1, 2]} spacing={4}>
            <MetricPanel>
              <Metric
                label={i18n.t('entity.warehouse.reporting.netConsumptionValue')}
                value={
                  props.classification.netConsumptionValue === null
                    ? EMPTY_PLACEHOLDER
                    : (formatCurrency(
                        props.classification.netConsumptionValue,
                        props.currency,
                        2
                      ) ?? EMPTY_PLACEHOLDER)
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.shareOfTotal')}
                value={percent(props.classification.sharePercent)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.cumulativeShare')}
                value={percent(props.classification.cumulativeSharePercent)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.abcRank')}
                value={number(props.classification.abcRank, 0)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.evaluationPeriod')}
                value={period(
                  props.classification.abcPeriodStart,
                  props.classification.abcPeriodEnd
                )}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.historyLength')}
                value={number(props.classification.abcHistoryMonthCount, 0)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.snapshotDate')}
                value={date(props.classification.abcSnapshotDate)}
              />
            </MetricPanel>
            <MetricPanel>
              <Metric
                label={i18n.t('entity.warehouse.reporting.meanMonthlyIssuedQuantity')}
                value={
                  props.classification.meanMonthlyQuantity === null
                    ? EMPTY_PLACEHOLDER
                    : number(props.classification.meanMonthlyQuantity, 2) +
                      ' ' +
                      props.handlingUnitLabel
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.coefficientOfVariation')}
                value={number(props.classification.coefficientOfVariation, 2)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.yearRepeatability')}
                value={number(props.classification.seasonalityCorrelation, 2)}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.activeMonths')}
                value={
                  props.classification.activeMonthCount === null ||
                  props.classification.xyzHistoryMonthCount === null
                    ? EMPTY_PLACEHOLDER
                    : number(props.classification.activeMonthCount, 0) +
                      ' / ' +
                      number(props.classification.xyzHistoryMonthCount, 0)
                }
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.evaluationPeriod')}
                value={period(
                  props.classification.xyzPeriodStart,
                  props.classification.xyzPeriodEnd
                )}
              />
              <Metric
                label={i18n.t('entity.warehouse.reporting.snapshotDate')}
                value={date(props.classification.xyzSnapshotDate)}
              />
            </MetricPanel>
          </Grid>
        </VStack>
      )}
    </Card>
  );
}
